import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Table,
  Switch,
  Button,
  Input,
  Spin,
  Tooltip,
  Space,
  Dropdown,
  Menu,
  Checkbox,
  Pagination,
} from 'antd';
import styles from './sources.module.sass';
import Tag from '../../../../shared-components/Tag';
import { ReactComponent as filter_funnel_icon } from '../../../../assets/icons/FilterFunnelIcon.svg';
import { ReactComponent as shared_icon } from '../../../../assets/icons/SharedIcon.svg';
import { ReactComponent as global_feed_icon } from '../../../../assets/icons/GlobalFeedIcon.svg';
import { ReactComponent as description_icon } from '../../../../assets/icons/notes.svg';
import { ReactComponent as no_results } from '../../../../assets/icons/no_results.svg';
import { ColumnTitle } from '../../../../shared-components/ColumnTitle';
import { STATUS } from '../../../../redux/constants';
import { SearchOutlined, UserOutlined, DownOutlined } from '@ant-design/icons';
import mixpanel from 'mixpanel-browser';
import { confirmDeleteSharedSource } from '../../../../shared-components/DeleteConfirmSharedSource';
import { confirmDelete } from '../../../../shared-components/DeleteConfirm';
import CreateEmptyFeedModal from '../../../AddNewEmptyFeedContainer/components/CreateEmptyFeedModal';
import CreateURLFeedModal from '../../../AddNewURLFeedContainer/components';
import CreateFileFeedModal from '../../../AddNewFileFeedContainer/components/CreateFileFeedModal';
import VersionCheck from '../../../../shared-components/VersionCheck';
import { changeTitle } from '../../../../redux/actions/app';
import { useDispatch } from 'react-redux';
import Icon, { MoreOutlined } from '@ant-design/icons';
import PermissionDeniedPage from '../../../../shared-components/PermissionDeniedPage';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import LoadingSilentPush from '../../../../shared-components/LoadingSilentPush';
import { incrementUIRequestsCounter } from '../../../../redux/utils/websocket/incrementUIRequestsCounter';

const GlobalFeedIcon = (props) => (
  <Icon style={{ fontSize: '10px' }} component={global_feed_icon} {...props} />
);

const DescriptionIcon = (props) => (
  <Icon style={{ fontSize: '14px' }} component={description_icon} {...props} />
);

const SharedIcon = (props) => (
  <Icon style={{ fontSize: '14px' }} component={shared_icon} {...props} />
);

const NoDataIcon = (props) => <Icon component={no_results} {...props} />;

const Sources = (props) => {
  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation();
  const { loadSourcesStatus } = props;
  const [dataSource, setDataSource] = useState([]);
  const selectedScope = useRef('all');
  const selectedState = useRef('feed');
  const [selectedSourceToEdit, setSelectedSourceToEdit] = useState({});
  const queryParams = new URLSearchParams(props.location.search);

  const currentPage = useRef(1);
  const pageFirstAccess = useRef(true);
  const showCreateEmptyFeedModal = useRef(false);
  const showCreateURLFeedModal = useRef(false);
  const showCreateFileFeedModal = useRef(false);
  const filterWithReport = useRef(false);

  const [tableFilter, setTableFilter] = useState({
    source_name_filter: '',
    type_filter: {
      is_domain_checked: false,
      is_ip_checked: false,
      is_url_checked: false,
    },
    data_source_filter: {
      is_manual: false,
      is_url: false,
    },
    source_vendor_filter: '',
    kick_search_off: false,
    sorter: {},
  });

  const refreshDataInterval = useRef(null);
  const pageHasFocus = useRef(true);

  const startRefreshInterval = () => {
    if (
      props.history.location.pathname.includes('/sources/feed') ||
      props.history.location.pathname.includes('/sources/collection')
    ) {
      refreshDataInterval.current = setInterval(() => {
        if (pageHasFocus.current) {
          fetchSourcesCounts();
        }
      }, 15000);
    }
  };

  useEffect(() => {
    props.loadMySubscriptionPlan();
    props.loadFilters();

    window.addEventListener('blur', function () {
      pageHasFocus.current = false;
    });

    window.addEventListener(
      'focus',
      function () {
        pageHasFocus.current = true;
        clearInterval(refreshDataInterval.current);
        startRefreshInterval();
      },
      true,
    );

    return () => {
      clearInterval(refreshDataInterval.current);
    };
  }, []);

  useEffect(() => {
    if (tableFilter?.kick_search_off) {
      fetchData();
    }
  }, [tableFilter]);

  useEffect(() => {
    // if (location?.search.includes('feedFromFile')) {
    //   showCreateFileFeedModal.current = true;
    // } else if (location?.search.includes('feedFromUrl')) {
    //   showCreateURLFeedModal.current = true;
    // } else if (location?.search.includes('feedFromEmpty')) {
    //   showCreateEmptyFeedModal.current = true;
    // }

    currentPage.current = 1;
    if (queryParams.get('scope')) {
      selectedScope.current = queryParams.get('scope').toLocaleLowerCase();
    }
    if (params['state']) {
      selectedState.current = params['state'].toLocaleLowerCase();
    }
    clearFilters();
    document.title = `${capitalizeFirstLetter(
      selectedState.current,
    )}s - Silent Push`;
    dispatch(
      changeTitle(capitalizeFirstLetter(selectedState.current) + 's', null),
    );
    pageFirstAccess.current = false;
  }, [location]);

  useEffect(() => {
    setDataSource(props?.sources);
    clearInterval(refreshDataInterval.current);
    startRefreshInterval();
    return () => {
      clearInterval(refreshDataInterval.current);
    };
  }, [props?.sources]);

  useEffect(() => {
    if (props?.loadSourcesStatus === STATUS.LOADED) {
      fetchSourcesCounts();
    }
  }, [props?.loadSourcesStatus]);

  const getSourceIOCFilterString = () => {
    let sourceIOCTypeFilter = '';
    if (tableFilter?.type_filter) {
      if (tableFilter?.type_filter?.is_domain_checked) {
        sourceIOCTypeFilter += ',domain';
      }
      if (tableFilter?.type_filter?.is_ip_checked) {
        sourceIOCTypeFilter += ',ip';
      }
      if (tableFilter?.type_filter?.is_url_checked) {
        sourceIOCTypeFilter += ',url';
      }
    }
    return sourceIOCTypeFilter;
  };

  const getSourceDataSourceFilterString = () => {
    let sourceDataSourceFilter = '';
    if (tableFilter?.data_source_filter?.is_manual) {
      sourceDataSourceFilter += ',MANUAL';
    }
    if (tableFilter?.data_source_filter?.is_url) {
      sourceDataSourceFilter += ',URL';
    }
    return sourceDataSourceFilter;
  };

  const getSortingParams = () => {
    let sortingParams = null;
    if (tableFilter?.sorter && tableFilter?.sorter?.order) {
      sortingParams =
        (tableFilter?.sorter?.order === 'descend' ? '-' : '') +
        tableFilter?.sorter?.columnKey;
    }
    return sortingParams;
  };

  const fetchSources = () => {
    let sourceIOCTypeFilter = getSourceIOCFilterString();
    let sourceDataSourceFilter = getSourceDataSourceFilterString();
    let sortingParams = getSortingParams();

    props.loadSources(
      selectedState.current,
      selectedScope.current,
      currentPage.current,
      tableFilter?.source_name_filter,
      sourceIOCTypeFilter,
      sourceDataSourceFilter,
      tableFilter?.source_vendor_filter,
      sortingParams,
    );
  };

  const fetchSourcesTagsIsActiveAndIOCCount = () => {
    let sourceIOCTypeFilter = getSourceIOCFilterString();
    let sourceDataSourceFilter = getSourceDataSourceFilterString();
    let sortingParams = getSortingParams();

    props.loadSourcesIocCountsAndIsActive(
      selectedState.current,
      selectedScope.current,
      currentPage.current,
      tableFilter?.source_name_filter,
      sourceIOCTypeFilter,
      sourceDataSourceFilter,
      tableFilter?.source_vendor_filter,
      sortingParams,
    );
  };

  const fetchSourcesCounts = () => {
    let sourceIOCTypeFilter = getSourceIOCFilterString();
    let sourceDataSourceFilter = getSourceDataSourceFilterString();
    let sortingParams = getSortingParams();

    props.loadSourcesIocCounts(
      selectedState.current,
      selectedScope.current,
      currentPage.current,
      tableFilter?.source_name_filter,
      sourceIOCTypeFilter,
      sourceDataSourceFilter,
      tableFilter?.source_vendor_filter,
      sortingParams,
    );
  };

  //@FIXME This is not ideal as this loads the table data but it's a sideeffect. This should be moved to a useEffect
  const fetchData = () => {
    fetchSources();
    fetchSourcesTagsIsActiveAndIOCCount();
    incrementUIRequestsCounter();
  };

  function capitalizeFirstLetter(string) {
    return string?.[0]?.toUpperCase() + string?.slice(1);
  }

  const isTagsLengthAbove30 = (tags) => {
    let length = 0;
    let count = 0;
    if (tags && tags?.length)
      for (const tag of tags) {
        length += tag?.length;
        count++;
        if (length > 30) return true;
        if (count > 3) return true;
      }
    return false;
  };

  const getFirstThreeTags = (tags) => {
    let firstTags = [];
    let length = 0;
    let count = 0;
    if (tags && tags?.length)
      for (const tag of tags) {
        length += tag?.length;
        if (length < 30 && count < 3) {
          firstTags.push(tag);
          count += 1;
        } else {
          break;
        }
      }
    return (
      <>
        {firstTags.map(
          (tag) =>
            tag && (
              <Tag
                tag={{ name: tag }}
                canClick={
                  props.mySubscriptionPlan?.has_access_to_threat_ranking
                    ? true
                    : false
                }
                isTagManuallyCreated={false}
                canRemove={false}
                showTooltip={
                  !props.mySubscriptionPlan?.has_access_to_threat_ranking
                }
                tooltipText={
                  <>
                    Filtering on tags is not part of this subscription plan. To
                    upgrade your subscription contact us at{' '}
                    <a target="_blank" href="mailto:sales@silentpush.com">
                      sales@silentpush.com
                    </a>
                    .
                  </>
                }
              />
            ),
        )}
        <Tooltip
          title={tags.map(
            (tag) =>
              tag && (
                <Tag
                  tag={{ name: tag }}
                  canClick={
                    props.mySubscriptionPlan?.has_access_to_threat_ranking
                      ? true
                      : false
                  }
                  isTagManuallyCreated={false}
                  canRemove={false}
                  showTooltip={
                    !props.mySubscriptionPlan?.has_access_to_threat_ranking
                  }
                  tooltipText={
                    <>
                      Filtering on tags is not part of this subscription plan.
                      To upgrade your subscription contact us at{' '}
                      <a target="_blank" href="mailto:sales@silentpush.com">
                        sales@silentpush.com
                      </a>
                      .
                    </>
                  }
                />
              ),
          )}
        >
          <MoreOutlined className={styles.moreTagsIndicator} />
        </Tooltip>
      </>
    );
  };

  const handlePublishSource = (record) => {
    props.publishSource({
      uuid: record?.uuid,
    });
    incrementUIRequestsCounter();
  };

  const handleMakePublic = (data) => {
    let requiredScope = '';
    if (data?.scope === 'USER' || data?.scope === 'ORGANISATION') {
      requiredScope = 'GLOBAL';
    } else {
      requiredScope = 'USER';
    }
    const shareObj = {
      uuid: data.uuid,
      scope: requiredScope,
    };
    props.changeSourceScope(shareObj);
    incrementUIRequestsCounter();
    mixpanel.track('Source Made Public');
  };

  const handleShareClick = (data) => {
    const shareObj = {
      uuid: data.uuid,
      scope: data?.scope === 'USER' ? 'ORGANISATION' : 'USER',
    };
    props.changeSourceScope(shareObj);
    incrementUIRequestsCounter();
    mixpanel.track('Source Shared', { feed_type: data?.type });
    fetchSourcesCounts();
  };

  const deleteSource = (record) => {
    if (record?.scope === 'ORGANISATION') {
      confirmDeleteSharedSource({
        source: record,
        onOk: () => {
          props.deleteSource(record?.uuid);
          incrementUIRequestsCounter();
          mixpanel.track('Source Deleted', { feed_type: record?.type });
        },
      });
    } else {
      confirmDelete({
        source: record,
        onOk: () => {
          props.deleteSource(record?.uuid);
          incrementUIRequestsCounter();
          mixpanel.track('Source Deleted', { feed_type: record?.type });
        },
      });

      mixpanel.track('Source Deleted', { feed_type: record?.type });
    }
  };

  const handleEditClick = (record) => {
    setSelectedSourceToEdit(record);
    if (record?.data_source === 'MANUAL') {
      showCreateFileFeedModal.current = true;
    } else {
      showCreateURLFeedModal.current = true;
    }
    mixpanel.track('Source Metadata Edit Attempt', { feed_type: record?.type });
  };

  const handleExportClick = (uuid) => {
    props.exportFeed(uuid);
    incrementUIRequestsCounter();
    let feed = props?.sources?.find((f) => {
      return f?.uuid == uuid;
    });
    mixpanel.track('source Downloaded', { feed_type: feed?.type });
  };

  const handleCollectionManagementClick = (record) => {
    props.history.push(
      '/source-management?scope=' +
        selectedScope.current +
        '&source_uuid=' +
        record?.uuid +
        `&backLink=` +
        encodeURIComponent(window.location.pathname) +
        encodeURIComponent(window.location.search),
    );
  };

  const DropdownMenu = (record) => {
    return (
      <Menu>
        {record?.record?.user === props?.user?.id &&
        selectedState.current === 'collection' ? (
          <Menu.Item onClick={() => handlePublishSource(record?.record)}>
            Promote to Feed
          </Menu.Item>
        ) : (
          ''
        )}

        {record?.record?.user === props?.user?.id &&
        (props?.user?.role === 'staff' || props?.user?.role === 'admin') &&
        selectedState.current === 'feed' ? (
          <Menu.Item onClick={() => handleMakePublic(record?.record)}>
            {record?.record?.scope === 'GLOBAL'
              ? 'Make Private'
              : 'Make Public'}
          </Menu.Item>
        ) : (
          ''
        )}

        {record?.record?.user === props?.user?.id ? (
          props.mySubscriptionPlan?.has_access_to_share_unshare ? (
            <Menu.Item
              aria-label="shareOrg"
              onClick={() => handleShareClick(record?.record)}
            >
              {record?.record?.scope === 'USER'
                ? 'Share with Organization'
                : 'Unshare with Organization'}
            </Menu.Item>
          ) : (
            <Tooltip
              title={
                props.mySubscriptionPlan?.has_access_to_share_unshare ? (
                  ''
                ) : (
                  <>
                    Sharing option is not part of this subscription plan. To
                    upgrade your subscription contact us at{' '}
                    <a target="_blank" href="mailto:sales@silentpush.com">
                      sales@silentpush.com
                    </a>
                    .
                  </>
                )
              }
              placement="left"
            >
              <Menu.Item aria-label="shareOrg" className={styles.disabled}>
                Share with Organization
              </Menu.Item>
            </Tooltip>
          )
        ) : (
          ''
        )}

        {(record?.record?.scope === 'USER' &&
          record?.record?.user === props?.user?.id) ||
        (record?.record?.scope === 'ORGANISATION' &&
          record?.record?.organisation === props?.user?.organisation_id) ||
        (record?.record?.scope === 'GLOBAL' &&
          record?.record?.user === props?.user?.id) ? (
          <Menu.Item
            aria-label={'editBtn'}
            onClick={() => handleEditClick(record?.record)}
          >
            Edit
          </Menu.Item>
        ) : (
          ''
        )}
        {(record?.record?.scope === 'USER' &&
          record?.record?.user === props?.user?.id) ||
        (record?.record?.scope === 'ORGANISATION' &&
          record?.record?.organisation === props?.user?.organisation_id) ? (
          <Menu.Item
            aria-label={'downExp'}
            onClick={() => handleExportClick(record?.record?.uuid)}
          >
            Download (Export)
          </Menu.Item>
        ) : (
          ''
        )}

        <Menu.Item
          disabled={record?.record?.data_source !== 'MANUAL'}
          aria-label={'feedBtn'}
          onClick={() => handleCollectionManagementClick(record?.record)}
        >
          {selectedState.current === 'feed' ? 'Feed' : 'Collection'} Management
        </Menu.Item>

        {(record?.record?.scope === 'USER' &&
          record?.record?.user === props?.user?.id) ||
        (record?.record?.scope === 'ORGANISATION' &&
          record?.record?.organisation === props?.user?.organisation_id) ||
        (record?.record?.scope === 'GLOBAL' &&
          record?.record?.user === props?.user?.id) ? (
          <Menu.Item
            aria-label={'deleteOrgFeed'}
            onClick={() => {
              deleteSource(record?.record);
            }}
          >
            <p style={{ color: 'red' }}>Delete</p>
          </Menu.Item>
        ) : (
          ''
        )}
      </Menu>
    );
  };

  const clearFilters = () => {
    setTableFilter({
      source_name_filter: '',
      type_filter: {
        is_domain_checked: false,
        is_ip_checked: false,
        is_url_checked: false,
      },
      data_source_filter: {
        is_manual: false,
        is_url: false,
      },
      source_vendor_filter: '',
      kick_search_off: true,
      sorter: {},
    });
  };

  const getSourceNameSearchProps = () => ({
    filterDropdown: ({ confirm }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search name`}
          value={tableFilter?.source_name_filter}
          onChange={(e) => {
            if (e.target.value === '') {
              setTableFilter({
                ...tableFilter,
                source_name_filter: e.target.value,
                kick_search_off: true,
              });
            } else {
              setTableFilter({
                ...tableFilter,
                source_name_filter: e.target.value,
                kick_search_off: false,
              });
            }
          }}
          onPressEnter={() => {
            currentPage.current = 1;
            fetchData();
            confirm();
          }}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            onClick={(e) => {
              setTableFilter({
                ...tableFilter,
                source_name_filter: '',
                kick_search_off: true,
              });
              confirm();
            }}
            size="small"
            style={{ width: 90, border: '1px solid rgb(60, 154, 232)' }}
          >
            Clear
          </Button>
          <Button
            type="primary"
            onClick={() => {
              currentPage.current = 1;
              fetchData();
              confirm();
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
        </Space>
      </div>
    ),
  });

  const getSourceVendorSearchProps = () => ({
    filterDropdown: ({ confirm }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search name`}
          value={tableFilter?.source_vendor_filter}
          onChange={(e) => {
            if (e.target.value === '') {
              setTableFilter({
                ...tableFilter,
                source_vendor_filter: e.target.value,
                kick_search_off: true,
              });
            } else {
              setTableFilter({
                ...tableFilter,
                source_vendor_filter: e.target.value,
                kick_search_off: false,
              });
            }
          }}
          onPressEnter={(e) => {
            currentPage.current = 1;
            fetchData();
            confirm();
          }}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            onClick={() => {
              setTableFilter({
                ...tableFilter,
                source_vendor_filter: '',
                kick_search_off: true,
              });
              confirm();
            }}
            size="small"
            style={{ width: 90, border: '1px solid rgb(60, 154, 232)' }}
          >
            Clear
          </Button>
          <Button
            type="primary"
            onClick={() => {
              currentPage.current = 1;
              fetchData();
              confirm();
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
        </Space>
      </div>
    ),
  });

  const getSourceIOCTypeSearchProps = () => ({
    filterDropdown: ({ confirm }) => (
      <div style={{ padding: 8 }}>
        <Checkbox
          className="filter_option"
          checked={tableFilter?.type_filter?.is_domain_checked}
          onChange={(event) => {
            setTableFilter({
              ...tableFilter,
              type_filter: {
                ...tableFilter?.type_filter,
                is_domain_checked: event.target.checked,
              },
              kick_search_off: false,
            });
          }}
        >
          Domain
        </Checkbox>
        <br />
        <Checkbox
          className="filter_option"
          checked={tableFilter?.type_filter?.is_ip_checked}
          onChange={(event) => {
            setTableFilter({
              ...tableFilter,
              type_filter: {
                ...tableFilter?.type_filter,
                is_ip_checked: event.target.checked,
              },
              kick_search_off: false,
            });
          }}
        >
          IP
        </Checkbox>
        <br />
        <Checkbox
          className="filter_option"
          checked={tableFilter?.type_filter?.is_url_checked}
          onChange={(event) => {
            setTableFilter({
              ...tableFilter,
              type_filter: {
                ...tableFilter?.type_filter,
                is_url_checked: event.target.checked,
              },
              kick_search_off: false,
            });
          }}
        >
          URL
        </Checkbox>
        <br />
        <Space>
          <Button
            onClick={() => {
              setTableFilter({
                ...tableFilter,
                type_filter: {
                  is_domain_checked: false,
                  is_ip_checked: false,
                  is_url_checked: false,
                },
                kick_search_off: true,
              });
              confirm();
            }}
            size="small"
            style={{ width: 90, border: '1px solid rgb(60, 154, 232)' }}
          >
            clear
          </Button>
          <Button
            type="primary"
            onClick={() => {
              currentPage.current = 1;
              fetchData();
              confirm();
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),
  });

  const getSourceDataSourceSearchProps = () => ({
    filterDropdown: ({ confirm }) => (
      <div style={{ padding: 8 }}>
        <Checkbox
          className="filter_option"
          checked={tableFilter?.data_source_filter?.is_manual}
          onChange={(event) => {
            setTableFilter({
              ...tableFilter,
              data_source_filter: {
                ...tableFilter?.data_source_filter,
                is_manual: event.target.checked,
              },
              kick_search_off: false,
            });
          }}
        >
          Manual
        </Checkbox>
        <br />
        <Checkbox
          className="filter_option"
          checked={tableFilter?.data_source_filter?.is_url}
          onChange={(event) => {
            setTableFilter({
              ...tableFilter,
              data_source_filter: {
                ...tableFilter?.data_source_filter,
                is_url: event.target.checked,
              },
              kick_search_off: false,
            });
          }}
        >
          URL
        </Checkbox>
        <br />
        <Space>
          <Button
            onClick={() => {
              setTableFilter({
                ...tableFilter,
                data_source_filter: {
                  is_manual: false,
                  is_url: false,
                },
                kick_search_off: true,
              });
              confirm();
            }}
            size="small"
            style={{ width: 90, border: '1px solid rgb(60, 154, 232)' }}
          >
            clear
          </Button>
          <Button
            type="primary"
            onClick={() => {
              currentPage.current = 1;
              fetchData();
              confirm();
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),
  });

  const FilterFunnelIcon = (props) => (
    <Icon
      style={{
        fontSize: '16px',
      }}
      component={filter_funnel_icon}
      {...props}
    />
  );

  const columns = [
    {
      title: (
        <ColumnTitle
          columnName="Show on Threat Ranking"
          tooltipText="Show on Threat Ranking"
        />
      ),
      align: 'center',
      width: 100,
      render: (record) => (
        <Switch
          checked={record?.is_active}
          loading={record?.is_active === null}
          key={record?.uuid}
          onChange={async () => {
            await props.activateSource({
              uuid: record?.uuid,
              is_active: !record?.is_active,
            });
            incrementUIRequestsCounter();
          }}
        />
      ),
    },
    {
      title: (
        <ColumnTitle
          columnName="Source Name"
          tooltipText="The name of the feed. Tip: Click on the feed name to filter Threat Ranking page by selected source."
        />
      ),
      dataIndex: 'name',
      align: 'left',
      key: 'name',
      filterIcon: (filtered) => (
        <FilterFunnelIcon
          style={{
            fontSize: '24px',
            position: 'absolute',
            left: '-5px',
            color: tableFilter?.source_name_filter ? '#1890ff' : undefined,
          }}
        />
      ),
      sorter: true,
      sortOrder:
        tableFilter?.sorter?.columnKey === 'name' && tableFilter?.sorter?.order,
      ...getSourceNameSearchProps(),
      render: (text, record, index) => {
        return (
          <>
            <span style={{ marginLeft: '0px', marginRight: '8px' }}>
              {record?.scope === 'USER' && record?.user === props?.user?.id ? (
                <UserOutlined />
              ) : record?.scope === 'ORGANISATION' ? (
                record?.creator ? (
                  <Tooltip
                    overlayStyle={{ whiteSpace: 'pre-line' }}
                    title={`Created by:
                        ${record?.creator}`}
                  >
                    <SharedIcon />
                  </Tooltip>
                ) : (
                  <SharedIcon />
                )
              ) : record?.creator ? (
                <Tooltip
                  overlayStyle={{ whiteSpace: 'pre-line' }}
                  title={`Created by:
                          ${record?.creator}`}
                >
                  <GlobalFeedIcon />
                </Tooltip>
              ) : (
                <GlobalFeedIcon />
              )}
            </span>
            <Link
              to={
                selectedState.current === 'collection'
                  ? '/?feed=' + record?.uuid + '&feed_state=' + record?.state
                  : '/?feed=' + record?.uuid
              }
              style={{
                color: '#3C9AE8',
                textDecorationLine: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                props.closeFilterSidebar();
                props.applyFilters(null);
                props.updateTag(null);
                props.updateIocList({
                  pagination: props?.ThreatRankingListPagination,
                  sorting: props?.ThreatRankingListSorting,
                  filters: {
                    feed: [record?.uuid],
                  },
                });
                mixpanel.track('Feed Viewed');
              }}
            >
              {record?.name}
            </Link>
            <span style={{ marginLeft: '8px', marginRight: '8px' }}>
              {record?.feed_description ? (
                <>
                  {' '}
                  <Tooltip placement="bottom" title={record?.feed_description}>
                    <DescriptionIcon />
                  </Tooltip>
                </>
              ) : (
                ''
              )}
            </span>
          </>
        );
      },
    },
    {
      title: (
        <ColumnTitle
          columnName="IOC Type"
          tooltipText="The type of the source"
        />
      ),
      dataIndex: 'type',
      key: 'type',
      align: 'left',
      ...getSourceIOCTypeSearchProps(),
      sorter: true,
      sortOrder:
        tableFilter?.sorter?.columnKey === 'type' && tableFilter?.sorter?.order,
      filterIcon: (filtered) => (
        <FilterFunnelIcon
          style={{
            fontSize: '24px',
            position: 'absolute',
            left: '-5px',
            color:
              tableFilter?.type_filter?.is_domain_checked ||
              tableFilter?.type_filter?.is_ip_checked ||
              tableFilter?.type_filter?.is_url_checked
                ? '#1890ff'
                : undefined,
          }}
        />
      ),
    },
    {
      title: (
        <ColumnTitle
          columnName="Data Source"
          tooltipText="The data source of the source"
        />
      ),
      dataIndex: 'data_source',
      key: 'data_source',
      align: 'left',
      ...getSourceDataSourceSearchProps(),
      sorter: true,
      sortOrder:
        tableFilter?.sorter?.columnKey === 'data_source' &&
        tableFilter?.sorter?.order,
      filterIcon: (filtered) => (
        <FilterFunnelIcon
          style={{
            fontSize: '24px',
            position: 'absolute',
            left: '-5px',
            color:
              tableFilter?.data_source_filter?.is_url ||
              tableFilter?.data_source_filter?.is_manual
                ? '#1890ff'
                : undefined,
          }}
        />
      ),
      render: (text) => {
        if (text === 'MANUAL') {
          return 'Manual';
        } else return text;
      },
    },
    {
      title: (
        <ColumnTitle
          columnName="Vendor"
          tooltipText="The vendor of the source. Tip: Click on the vendor to filter Threat Ranking page by selected vendor."
        />
      ),
      dataIndex: 'vendor',
      align: 'left',
      key: 'vendor',
      filterIcon: (filtered) => (
        <FilterFunnelIcon
          style={{
            fontSize: '24px',
            position: 'absolute',
            left: '-5px',
            color: tableFilter?.source_vendor_filter ? '#1890ff' : undefined,
          }}
        />
      ),
      ...getSourceVendorSearchProps(),
      sorter: true,
      sortOrder:
        tableFilter?.sorter?.columnKey === 'vendor' &&
        tableFilter?.sorter?.order,
      render: (text, record, index) => {
        return (
          <>
            <Link
              to={'/?vendor=' + record?.vendor + '&feed_state=' + record?.state}
              style={{
                color: '#3C9AE8',
                textDecorationLine: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                props.closeFilterSidebar();
                props.applyFilters(null);
                props.updateTag(null);
                props.updateIocList({
                  pagination: props?.ThreatRankingListPagination,
                  sorting: props?.ThreatRankingListSorting,
                  filters: {
                    vendor: [record?.vendor],
                  },
                });
                mixpanel.track('Source Viewed');
              }}
            >
              {record?.vendor}
            </Link>
            {/* {record?.vendor} */}
            <span style={{ marginLeft: '8px', marginRight: '8px' }}>
              {record?.vendor_description ? (
                <>
                  {' '}
                  <Tooltip
                    placement="bottom"
                    title={record?.vendor_description}
                  >
                    <DescriptionIcon />
                  </Tooltip>
                </>
              ) : (
                ''
              )}
            </span>
          </>
        );
      },
    },
    {
      title: (
        <ColumnTitle
          columnName="Count"
          tooltipText="The observable count of the source"
        />
      ),
      dataIndex: 'ioc_count',
      align: 'center',
      key: 'ioc_count',
      width: '120px',
      render: (record) => (
        <Spin spinning={record === null} size="small">
          {record?.toLocaleString()}
        </Spin>
      ),
    },
    {
      title: (
        <ColumnTitle
          columnName="Status"
          tooltipText="The current status of the source"
        />
      ),
      dataIndex: 'collection_status',
      align: 'center',
      key: 'collection_status',
      width: '120px',
      render: (record) => (
        <Spin spinning={!record} size="small">
          {record}
        </Spin>
      ),
    },
    {
      title: <ColumnTitle columnName="Tags" tooltipText="The source tags" />,
      dataIndex: 'tags',
      align: 'left',
      key: 'tags',
      render: (text, record, index) => {
        return (
          <Spin spinning={record?.tags === null} size="small">
            <>
              <span
                style={{
                  color: '#3C9AE8',
                }}
              >
                {isTagsLengthAbove30(record?.tags)
                  ? getFirstThreeTags(record?.tags)
                  : record?.tags?.map(
                      (tag) =>
                        tag && (
                          <Tag
                            tag={{ name: tag }}
                            canClick={
                              props.mySubscriptionPlan
                                ?.has_access_to_threat_ranking
                                ? true
                                : false
                            }
                            filterProfileToBeCleared={true}
                            isTagManuallyCreated={false}
                            canRemove={false}
                            extraParams={
                              selectedState.current === 'collection'
                                ? '&feed_state=COLLECTION'
                                : ''
                            }
                            showTooltip={
                              !props.mySubscriptionPlan
                                ?.has_access_to_threat_ranking
                            }
                            tooltipText={
                              <>
                                Filtering on tags is not part of this
                                subscription plan. To upgrade your subscription
                                contact us at{' '}
                                <a
                                  target="_blank"
                                  href="mailto:sales@silentpush.com"
                                >
                                  sales@silentpush.com
                                </a>
                                .
                              </>
                            }
                          />
                        ),
                    )}
              </span>
            </>
          </Spin>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (record) => (
        <div className={styles.action_buttons}>
          {record.has_tlp_amber_reports && (
            <Link to={`/blog?name=${record.uuid}`}>
              <Button
                type="link"
                onClick={() => {
                  mixpanel.track('Report Viewed');
                }}
              >
                View Report
              </Button>
            </Link>
          )}

          {props?.loadSourcesIocCountsAndIsActiveStatus !== STATUS.LOADED ? (
            <Spin
              className={styles.spinner}
              spinning={
                props?.loadSourcesIocCountsAndIsActiveStatus !== STATUS.LOADED
              }
              size="small"
            ></Spin>
          ) : (
            <Dropdown trigger={['click']} overlay={DropdownMenu({ record })}>
              <MoreOutlined className={styles.dropdown_icon} />
            </Dropdown>
          )}
        </div>
      ),
    },
  ];

  const addNewSourceMenu = (
    <Menu>
      <Menu.Item
        data-cy="sourcesContainerCreateNewFromFile"
        key="from-file"
        onClick={() => handleCreateFromFileClick()}
      >
        From File
      </Menu.Item>
      <Menu.Item
        data-cy="sourcesContainerCreateNewFromURL"
        key="from-url"
        onClick={() => handleCreateFromURLClick()}
      >
        From URL
      </Menu.Item>
      <Menu.Item
        data-cy="sourcesContainerCreateNewEmpty"
        key="empty"
        onClick={() => handleCreateEmptyClick()}
      >
        Empty
      </Menu.Item>
    </Menu>
  );

  const handleCreateFromFileClick = () => {
    showCreateFileFeedModal.current = true;
    setSelectedSourceToEdit({});
    sessionStorage.setItem('tags', []);
  };

  const handleCreateFromURLClick = () => {
    showCreateURLFeedModal.current = true;
    setSelectedSourceToEdit({});
    sessionStorage.setItem('tags', []);
  };

  const handleCreateEmptyClick = () => {
    showCreateEmptyFeedModal.current = true;
    setSelectedSourceToEdit({});
    sessionStorage.setItem('tags', []);
  };

  const setAllAsSelectedScope = () => {
    //currentPage.current = 1;
    return '/sources/' + selectedState.current + '?scope=all';
  };

  const setGlobalAsSelectedScope = () => {
    //currentPage.current = 1;
    return '/sources/' + selectedState.current + '?scope=global';
  };

  const setOrganizationAsSelectedScope = () => {
    //currentPage.current = 1;
    return '/sources/' + selectedState.current + '?scope=organisation';
  };

  const setMyAsSelectedScope = () => {
    //currentPage.current = 1;
    return '/sources/' + selectedState.current + '?scope=user';
  };

  const onCancel = () => {
    // props.history.replace({
    //   search: null,
    // });
    showCreateEmptyFeedModal.current = false;
    showCreateFileFeedModal.current = false;
    showCreateURLFeedModal.current = false;
    setSelectedSourceToEdit({});
    fetchSourcesCounts();
  };

  const onUpdate = () => {
    onCancel();
  };

  const onCreate = () => {
    // if (
    //   selectedScope.current === 'global' ||
    //   selectedScope.current === 'organisation'
    // ) {
    //   setMyAsSelectedScope();
    // }
    setAllAsSelectedScope();
    selectedScope.current = 'all';
    currentPage.current = 1;
    onCancel();
  };

  const handleTableChange = (pagination, filters, sort) => {
    setTableFilter({
      ...tableFilter,
      sorter: { columnKey: sort?.columnKey, order: sort?.order },
      kick_search_off: true,
    });
  };

  return props.mySubscriptionPlanStatus === STATUS.LOADED ? (
    props.mySubscriptionPlan?.has_access_to_sources ? (
      <div className={styles.sources}>
        <div className={styles.sourcesTableContainer}>
          <div className={styles.sourcesTableHeader}>
            <Input
              id="searchSourceName"
              name="searchSourceName"
              className={styles.sourceNameInput}
              type="text"
              value={tableFilter?.source_name_filter}
              onPressEnter={(e) => {
                currentPage.current = 1;
                fetchData();
              }}
              onChange={(e) => {
                if (e.target.value === '') {
                  setTableFilter({
                    ...tableFilter,
                    source_name_filter: e.target.value,
                    kick_search_off: true,
                  });
                } else {
                  setTableFilter({
                    ...tableFilter,
                    source_name_filter: e.target.value,
                    kick_search_off: false,
                  });
                }
              }}
              allowClear
              size="default"
              placeholder="Search Source name"
              prefix={
                <SearchOutlined style={{ color: 'rgba(255, 255, 255, 0.3)' }} />
              }
            />

            <div className={styles.actions}>
              <div className={styles.clearFiltersContainer}>
                <Tooltip title="Clear all active filters">
                  <Button type="link" size="small" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Tooltip>
              </div>

              <div>
                <Link to={setAllAsSelectedScope()}>
                  <Button
                    disabled={loadSourcesStatus !== STATUS.LOADED}
                    className={cx(styles.scopeButton, {
                      [styles.activeScopeButton]:
                        selectedScope.current === 'all',
                    })}
                    type="ghost"
                  >
                    All
                  </Button>
                </Link>

                {selectedState.current === 'feed' && (
                  <Link to={setGlobalAsSelectedScope()}>
                    <Button
                      disabled={loadSourcesStatus !== STATUS.LOADED}
                      className={cx(styles.scopeButton, {
                        [styles.activeScopeButton]:
                          selectedScope.current === 'global',
                      })}
                      type="ghost"
                    >
                      Global
                    </Button>
                  </Link>
                )}

                <Link to={setOrganizationAsSelectedScope()}>
                  <Button
                    disabled={loadSourcesStatus !== STATUS.LOADED}
                    className={cx(styles.scopeButton, {
                      [styles.activeScopeButton]:
                        selectedScope.current === 'organisation',
                    })}
                    type="ghost"
                  >
                    Organization
                  </Button>
                </Link>

                <Link to={setMyAsSelectedScope()}>
                  <Button
                    type="ghost"
                    className={cx(styles.scopeButton, {
                      [styles.activeScopeButton]:
                        selectedScope.current === 'user',
                    })}
                    disabled={loadSourcesStatus !== STATUS.LOADED}
                  >
                    {selectedState.current === 'feed'
                      ? 'My Feeds'
                      : 'My Collections'}
                  </Button>
                </Link>
              </div>
            </div>

            <div className={styles.createButton}>
              <Dropdown
                trigger={['click']}
                role={'button3'}
                type="primary"
                htmlType="submit"
                overlay={addNewSourceMenu}
              >
                <Button type="primary">
                  Create New{' '}
                  {selectedState.current === 'feed' ? 'Feed' : 'Collection'}
                  <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          </div>

          <Fragment>
            <Table
              className={styles.sourcesTable}
              rowKey={(record) => record?.uuid}
              columns={columns}
              dataSource={dataSource}
              pagination={{
                showSizeChanger: false,
                position: ['topRight', 'bottomRight'],
                total: props?.sourcesCount,
                current: currentPage.current,
                pageSize: props?.ThreatRankingListPagination?.pageSize,
                className: styles.feedsTablePagination,
                onChange: (current, pageSize) => {
                  currentPage.current = current;
                  window.scrollTo(0, 0);
                },
              }}
              onChange={handleTableChange}
              loading={loadSourcesStatus === STATUS.LOADING}
              locale={{
                emptyText:
                  props.collectionsLoadStatus === STATUS.LOADING ? (
                    <div style={{ height: '150px' }}></div>
                  ) : (
                    <div style={{ marginTop: '10px' }}>
                      {props?.collections && props?.collections?.length > 0 ? (
                        ''
                      ) : (
                        <span>
                          <NoDataIcon style={{ fontSize: 32 }} />
                          <p
                            style={{
                              color: 'rgba(255, 255, 255, 0.95)',
                              fontFamily: 'TT Hoves',
                              fontWeight: 600,
                              fontSize: '14px',
                              lineHeight: '19px',
                              fontStyle: 'normal',
                            }}
                          >
                            No{' '}
                            {selectedState.current === 'feed'
                              ? 'Feeds'
                              : 'Collections'}{' '}
                            yet. To set one up click on{' '}
                            <span>
                              Create New{' '}
                              {selectedState.current === 'feed'
                                ? 'Feed'
                                : 'Collection'}
                            </span>
                            .
                          </p>
                        </span>
                      )}
                    </div>
                  ),
              }}
            />
          </Fragment>

          {showCreateEmptyFeedModal.current ? (
            <CreateEmptyFeedModal
              state={selectedState.current}
              scope={selectedScope.current}
              createEmptySource={props.createEmptySource}
              addTag={props.addTag}
              foundTags={props.foundTags}
              searchStatus={props.searchStatus}
              searchForTags={props.searchForTags}
              resetTagSearch={props.resetTagSearch}
              onCancel={onCancel}
              onCreate={onCreate}
            />
          ) : (
            ''
          )}
          {showCreateURLFeedModal.current ? (
            <CreateURLFeedModal
              state={selectedState.current}
              scope={selectedScope.current}
              feedTable={props.feedTable}
              feedItemFields={props.feedItemFields}
              consumeStatus={props.consumeStatus}
              consumeFeedData={props.consumeFeedData}
              verifyAuth={props.verifyAuth}
              testStatus={props.testStatus}
              loadCollection={props.loadSource}
              createURLSource={props.createURLSource}
              updateURLSource={props.updateSource}
              addTag={props.addTag}
              foundTags={props.foundTags}
              searchStatus={props.searchStatus}
              searchForTags={props.searchForTags}
              resetTagSearch={props.resetTagSearch}
              selectedSourceToEdit={selectedSourceToEdit}
              setSelectedSourceToEdit={setSelectedSourceToEdit}
              onCancel={onCancel}
              onUpdate={onUpdate}
              onCreate={onCreate}
            />
          ) : (
            ''
          )}

          {showCreateFileFeedModal.current ? (
            <CreateFileFeedModal
              state={selectedState.current}
              feedTable={props.feedTable}
              feedItemFields={props.feedItemFields}
              consumeStatus={props.consumeStatus}
              consumeFileData={props.consumeFileData}
              loadCollection={props.loadSource}
              createFileSource={props.createFileSource}
              updateFileSource={props.updateFileSource}
              addTag={props.addTag}
              foundTags={props.foundTags}
              searchStatus={props.searchStatus}
              searchForTags={props.searchForTags}
              resetTagSearch={props.resetTagSearch}
              selectedSourceToEdit={selectedSourceToEdit}
              setSelectedSourceToEdit={setSelectedSourceToEdit}
              onCancel={onCancel}
              onUpdate={onUpdate}
              onCreate={onCreate}
            />
          ) : (
            ''
          )}
        </div>
        <VersionCheck parentComponentStatus={props.loadSourcesStatus} />
      </div>
    ) : (
      <PermissionDeniedPage />
    )
  ) : (
    <LoadingSilentPush size={80} positionCenter />
  );
};

export default Sources;
